/**
 * GuidedModeFlow Component
 *
 * Provides guided question flow for proposal discussions.
 *
 * Story FOS-4.2.3: Leaf Components Batch 1 (Stage/Quality)
 *
 * Features:
 * - Progressive question disclosure
 * - Guided questions: "What problem?", "Who benefits?", "How solved?", "Why now?"
 * - Save responses to discussion metadata
 * - Skip to free-form option
 *
 * @module components/governance/GuidedModeFlow
 * @see AC-4.1.5.1 - Users can create new discussions with guided question flow
 */

import { useState, useCallback, useMemo } from 'react';
import { ChevronRight, Check, SkipForward, Lightbulb } from 'lucide-react';
import { DraftAssistPanel } from './DraftAssistPanel';

/**
 * Guided question definition
 */
interface GuidedQuestion {
  id: string;
  prompt: string;
  placeholder: string;
  hint: string;
  minLength: number;
}

/**
 * Type for guided mode answers/responses
 */
export type GuidedAnswers = Record<string, string>;

/**
 * Props for GuidedModeFlow component
 */
export interface GuidedModeFlowProps {
  /** Callback when all questions are answered */
  onComplete: (responses: GuidedAnswers) => void;
  /** Callback when user skips to free-form */
  onSkip?: () => void;
  /** Initial responses (for editing) */
  initialResponses?: GuidedAnswers;
  /** Initial answers (alias for initialResponses) */
  initialAnswers?: GuidedAnswers;
  /** Optional class name */
  className?: string;
}

/**
 * Guided questions for proposal development
 */
const GUIDED_QUESTIONS: GuidedQuestion[] = [
  {
    id: 'problem',
    prompt: 'What problem are you trying to solve?',
    placeholder: 'Describe the issue, challenge, or opportunity you want to address...',
    hint: 'Be specific about the current situation and why it needs to change.',
    minLength: 50,
  },
  {
    id: 'beneficiaries',
    prompt: 'Who benefits from this proposal?',
    placeholder: 'Identify the stakeholders, members, or community segments that will benefit...',
    hint: 'Consider both direct and indirect beneficiaries.',
    minLength: 30,
  },
  {
    id: 'solution',
    prompt: 'How do you propose to solve it?',
    placeholder: 'Describe your proposed approach, solution, or action...',
    hint: 'Include key steps, resources needed, and expected outcomes.',
    minLength: 100,
  },
  {
    id: 'urgency',
    prompt: 'Why should this be addressed now?',
    placeholder: 'Explain the timing, urgency, or opportunity window...',
    hint: 'What happens if we delay? What opportunities might we miss?',
    minLength: 30,
  },
];

/**
 * Single question card component
 */
function QuestionCard({
  question,
  response,
  isActive,
  isCompleted,
  questionNumber,
  totalQuestions,
  onChange,
  onNext,
}: {
  question: GuidedQuestion;
  response: string;
  isActive: boolean;
  isCompleted: boolean;
  questionNumber: number;
  totalQuestions: number;
  onChange: (value: string) => void;
  onNext: () => void;
}) {
  const isValid = response.length >= question.minLength;
  const charCount = response.length;
  const charNeeded = question.minLength - charCount;

  if (!isActive && !isCompleted) {
    return null;
  }

  return (
    <div
      className={`rounded-lg border transition-all duration-300 ${
        isActive
          ? 'border-primary bg-card shadow-md'
          : isCompleted
            ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20'
            : 'border-border bg-muted/30'
      }`}
      data-testid={`question-card-${question.id}`}
      data-active={isActive.toString()}
      data-completed={isCompleted.toString()}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border/50">
        <div className="flex items-center gap-3">
          <span
            className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium ${
              isCompleted
                ? 'bg-green-500 text-white'
                : isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
            }`}
          >
            {isCompleted ? <Check className="h-4 w-4" /> : questionNumber}
          </span>
          <span className="text-sm text-muted-foreground">
            Question {questionNumber} of {totalQuestions}
          </span>
        </div>
        {isCompleted && (
          <span className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
            <Check className="h-3 w-3" />
            Completed
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        <h3 className="text-lg font-medium text-foreground">{question.prompt}</h3>

        {isActive && (
          <>
            {/* Hint */}
            <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-200">{question.hint}</p>
            </div>

            {/* Textarea */}
            <textarea
              value={response}
              onChange={(e) => onChange(e.target.value)}
              placeholder={question.placeholder}
              className="w-full min-h-[120px] p-3 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-y"
              data-testid={`question-input-${question.id}`}
            />

            {/* Character counter */}
            <div className="flex items-center justify-between text-xs">
              <span className={charNeeded > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}>
                {charNeeded > 0 ? `${charNeeded} more characters needed` : 'Minimum met ✓'}
              </span>
              <span className="text-muted-foreground">{charCount} characters</span>
            </div>

            {/* Next button */}
            <button
              onClick={onNext}
              disabled={!isValid}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                isValid
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              }`}
              data-testid={`question-next-${question.id}`}
            >
              {questionNumber === totalQuestions ? 'Complete' : 'Continue'}
              <ChevronRight className="h-4 w-4" />
            </button>
          </>
        )}

        {isCompleted && !isActive && (
          <p className="text-sm text-muted-foreground line-clamp-2">{response}</p>
        )}
      </div>
    </div>
  );
}

/**
 * GuidedModeFlow - Progressive question-driven proposal development
 *
 * @example
 * ```tsx
 * <GuidedModeFlow
 *   onComplete={(responses) => console.log('Completed:', responses)}
 *   onSkip={() => console.log('Skipped to free-form')}
 * />
 * ```
 */
export function GuidedModeFlow({
  onComplete,
  onSkip,
  initialResponses,
  initialAnswers,
  className = '',
}: GuidedModeFlowProps) {
  // Support both initialResponses and initialAnswers prop names
  const [responses, setResponses] = useState<GuidedAnswers>(initialResponses ?? initialAnswers ?? {});
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleResponseChange = useCallback((questionId: string, value: string) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const handleNext = useCallback(() => {
    if (currentIndex < GUIDED_QUESTIONS.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      onComplete(responses);
    }
  }, [currentIndex, responses, onComplete]);

  // BL-045.4: Compute concatenated content for DraftAssistPanel
  const currentContent = useMemo(
    () => Object.values(responses).join('\n\n'),
    [responses],
  );
  const hasAnsweredContent = Object.values(responses).some((v) => v.trim().length > 0);

  const progress = ((currentIndex + 1) / GUIDED_QUESTIONS.length) * 100;

  return (
    <div className={`space-y-6 ${className}`} data-testid="guided-mode-flow">
      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-foreground">Guided Discussion</span>
          <span className="text-muted-foreground">
            {currentIndex + 1} of {GUIDED_QUESTIONS.length}
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
            data-testid="guided-progress-bar"
          />
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {GUIDED_QUESTIONS.map((question, index) => (
          <QuestionCard
            key={question.id}
            question={question}
            response={responses[question.id] || ''}
            isActive={index === currentIndex}
            isCompleted={index < currentIndex}
            questionNumber={index + 1}
            totalQuestions={GUIDED_QUESTIONS.length}
            onChange={(value) => handleResponseChange(question.id, value)}
            onNext={handleNext}
          />
        ))}
      </div>

      {/* BL-045.4: AI Draft Suggestions */}
      {hasAnsweredContent && (
        <DraftAssistPanel body={currentContent} minLength={50} />
      )}

      {/* Skip option */}
      {onSkip && (
        <div className="flex justify-center pt-4 border-t border-border">
          <button
            onClick={onSkip}
            className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="skip-guided-mode"
          >
            <SkipForward className="h-4 w-4" />
            Skip to free-form discussion
          </button>
        </div>
      )}
    </div>
  );
}

export default GuidedModeFlow;
